import './styles/CalendarCard.scss';

import { useLazyQuery } from '@apollo/client';
import { Divider, Grid, Paper, Typography } from '@mui/material';

import {
  endOfMonth,
  endOfWeek,
  isBefore,
  isWithinInterval,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import { useEffect } from 'react';
import ReactCalendar, { OnArgs } from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import QUERY from 'gql/queries/getAppointments.gql';

import { LinkButton } from 'app/components/LinkButton';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

export function CalendarCard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loadAppointments, { data }] = useLazyQuery<{
    appointments: { startDate: string; endDate: string }[];
  }>(QUERY, {
    fetchPolicy: 'no-cache',
    onError: error => {
      dispatch(
        AlertActions.error(`${t(translations.pages.calendar.openFailed())}: ${error.message}`)
      );
    },
    variables: {
      startDate: startOfWeek(startOfMonth(new Date())).toISOString(),
      endDate: endOfWeek(endOfMonth(new Date())).toISOString()
    }
  });

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const goToDay = (value: Date) => {
    navigate(`/calendar?day=${value.toISOString()}`);
  };

  const onRangeChange = (date: OnArgs) => {
    if (!date.activeStartDate) {
      return;
    }
    loadAppointments({
      variables: {
        startDate: startOfWeek(startOfMonth(date.activeStartDate)).toISOString(),
        endDate: endOfWeek(endOfMonth(date.activeStartDate)).toISOString()
      }
    });
  };

  const generateDotMarking = ({ date, view }) => {
    let appointmentsForDay: any[] = [];
    if (view === 'month' && data?.appointments && data?.appointments?.length !== 0) {
      for (const appointment of data.appointments) {
        if (
          isWithinInterval(date, {
            start: new Date(appointment.startDate),
            end: new Date(appointment.endDate)
          })
        ) {
          appointmentsForDay.push(appointment);
        }
      }
      if (appointmentsForDay.length !== 0) {
        return (
          <span style={{ display: 'flex', justifyContent: 'center' }}>
            {appointmentsForDay.find(appointment => appointment.article.type === 'general') !==
              undefined && <div className="calendar-card__body__appointment__general" />}
            {appointmentsForDay.find(appointment => appointment.article.type === 'event') !==
              undefined && <div className="calendar-card__body__appointment__event" />}
            {appointmentsForDay.find(appointment => appointment.article.type === 'study') !==
              undefined && <div className="calendar-card__body__appointment__study" />}
          </span>
        );
      }
    }
    return <div style={{ height: '6px' }} />;
  };

  const applyClasses = ({ date, view }) => {
    let appointmentsForDay: any[] = [];
    if (view === 'month' && data?.appointments && data?.appointments?.length !== 0) {
      for (const appointment of data.appointments) {
        const start = new Date(appointment.startDate);
        const end = new Date(appointment.endDate);
        if (isBefore(start, end) && isWithinInterval(date, { start, end })) {
          appointmentsForDay.push(appointment);
        }
      }
      if (appointmentsForDay.length !== 0) {
        if (
          appointmentsForDay.find(appointment => appointment.article.type === 'legal') &&
          appointmentsForDay.find(appointment => appointment.article.type === 'holiday')
        ) {
          return 'calendar-card__body__appointment__legalAndHoliday';
        } else if (appointmentsForDay.find(appointment => appointment.article.type === 'holiday')) {
          return 'calendar-card__body__appointment__holiday';
        } else if (appointmentsForDay.find(appointment => appointment.article.type === 'legal')) {
          return 'calendar-card__body__appointment__legal';
        }
      }
    }
  };

  return (
    <Grid container className="calendar-card" component={Paper} elevation={4}>
      <Grid container className="calendar-card__heading">
        {t(translations.pages.home.calendar.heading())}
      </Grid>
      <Grid container className="calendar-card__body">
        <ReactCalendar
          onActiveStartDateChange={date => onRangeChange(date)}
          onClickDay={goToDay}
          prev2Label={null}
          next2Label={null}
          showNeighboringMonth={false}
          className="calendar-card__body__react-calendar"
          tileContent={generateDotMarking}
          tileClassName={applyClasses}
        />
        <Grid item xs={12} style={{ width: '100%', padding: '0 1em 0 1em' }}>
          <Divider style={{ width: '100%' }} />
        </Grid>
        <Grid item container xs={12} className="calendar-card__body__legend">
          <Grid container>
            <Grid item xs={2} alignItems="center" justifyContent="center" container>
              <div className="calendar-card__body__legend__event__circle" />
            </Grid>
            <Grid item xs={10} alignItems="center" justifyContent="flex-start" container>
              <Typography className="calendar-card__body__legend__text">
                {t(translations.pages.home.calendar.event())}
              </Typography>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={2} alignItems="center" justifyContent="center" container>
              <div className="calendar-card__body__legend__general__circle" />
            </Grid>
            <Grid item xs={10} alignItems="center" justifyContent="flex-start" container>
              <Typography className="calendar-card__body__legend__text">
                {t(translations.pages.home.calendar.general())}
              </Typography>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={2} alignItems="center" justifyContent="center" container>
              <div className="calendar-card__body__legend__study__circle" />
            </Grid>
            <Grid item xs={10} alignItems="center" justifyContent="flex-start" container>
              <Typography className="calendar-card__body__legend__text">
                {t(translations.pages.home.calendar.study())}
              </Typography>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={2} alignItems="center" justifyContent="center" container>
              <div className="calendar-card__body__legend__legal__square" />
            </Grid>
            <Grid item xs={10} alignItems="center" justifyContent="flex-start" container>
              <Typography className="calendar-card__body__legend__text">
                {t(translations.pages.home.calendar.legal())}
              </Typography>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={2} alignItems="center" justifyContent="center" container>
              <div className="calendar-card__body__legend__holiday__square" />
            </Grid>
            <Grid item xs={10} alignItems="center" justifyContent="flex-start" container>
              <Typography className="calendar-card__body__legend__text">
                {t(translations.pages.home.calendar.holiday())}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ width: '100%', padding: '0 1em 0 1em' }}>
          <Divider style={{ width: '100%' }} />
        </Grid>
        <LinkButton href="/calendar" linkText={t(translations.pages.home.calendar.action())} />
      </Grid>
    </Grid>
  );
}
